/**
 * havloFirebaseConfig.js
 * Secondary Firebase app connecting to Havlo's Firestore.
 * Used by the Support Queue (useLiveChats) and staff alerts (useStaffAlerts)
 * to read and answer Havlo chat conversations in real time.
 *
 * Havlo's rules only let workspace members list chats. TheScope180 staff get
 * a short-lived Havlo session from the havloStaffToken function (it checks the
 * TheScope180 role, then mints a Havlo custom token). ensureHavloStaffSession()
 * signs this secondary app in with it and keeps it fresh.
 *
 * Until the new Havlo rules are deployed, a failed token call just leaves the
 * app signed out, which the old rules still allow (temporary fallback).
 */
import { initializeApp } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import { getAuth, signInWithCustomToken, signOut, onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth as siteAuth, getFunctionsClient } from "./firebaseConfig";

const havloConfig = {
  apiKey: "AIzaSyB7Nu_j6cDdZWU-fw7pqrItXLtuZfjdxTw",
  authDomain: "havlo-c644c.firebaseapp.com",
  projectId: "havlo-c644c",
  storageBucket: "havlo-c644c.firebasestorage.app",
  messagingSenderId: "333888193262",
  appId: "1:333888193262:web:b372aef9f65dfadb5569ff",
};

// Initialize as secondary app (not default)
const havloApp = initializeApp(havloConfig, "havlo");
export const havloDb = getFirestore(havloApp);
export const havloAuth = getAuth(havloApp);

// Re-mint when less than this is left on the session (sessions last 8 hours)
const REFRESH_BEFORE_MS = 4 * 60 * 60 * 1000;
const CHECK_EVERY_MS = 5 * 60 * 1000;

let session = null; // { siteUid, expiresAt }
let pending = null;
let timer = null;

const expectedHavloUid = (siteUid) => `ts180_${siteUid}`;

async function mint(siteUid) {
  const call = httpsCallable(getFunctionsClient(), "havloStaffToken");
  const { data } = await call();
  await signInWithCustomToken(havloAuth, data.token);
  session = { siteUid, expiresAt: Number(data.expiresAt) || 0 };
  return true;
}

/**
 * Make sure the Havlo app is signed in as the current TheScope180 staff user.
 * Resolves true when a staff session is active, false otherwise (never throws).
 */
export function ensureHavloStaffSession() {
  const siteUid = siteAuth.currentUser?.uid;
  if (!siteUid) return Promise.resolve(false);
  const fresh =
    session &&
    session.siteUid === siteUid &&
    havloAuth.currentUser?.uid === expectedHavloUid(siteUid) &&
    session.expiresAt - Date.now() > REFRESH_BEFORE_MS;
  if (fresh) return Promise.resolve(true);
  if (pending) return pending;

  pending = mint(siteUid)
    .catch((err) => {
      console.warn("[Havlo] Staff session unavailable:", err.code || err.message);
      return false;
    })
    .finally(() => {
      pending = null;
    });

  if (!timer) {
    timer = setInterval(() => {
      if (session) ensureHavloStaffSession();
    }, CHECK_EVERY_MS);
  }
  return pending;
}

// Signing out of TheScope180 (or switching accounts) ends the Havlo session.
// A Havlo session saved in this browser for someone else is dropped too.
onAuthStateChanged(siteAuth, async () => {
  try {
    await havloAuth.authStateReady();
  } catch {
    /* ignore */
  }
  const current = havloAuth.currentUser;
  const user = siteAuth.currentUser; // latest, not the value from when this event fired
  if (!current) return;
  if (!user || current.uid !== expectedHavloUid(user.uid)) {
    session = null;
    signOut(havloAuth).catch(() => {});
  }
});
